import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../config";

// ── Палитра градиентов — детерминированно по userId ───────────────────────────
const AVATAR_GRADIENTS = [
  ["#6c63ff", "#a78bfa"], // фиолетовый
  ["#667eea", "#764ba2"], // индиго
  ["#4facfe", "#00c9ff"], // синий
  ["#43e97b", "#38f9d7"], // бирюзовый
  ["#fa709a", "#c471ed"], // розовый
  ["#f093fb", "#f5576c"], // малиновый
  ["#fccb90", "#d57eeb"], // персиковый
  ["#a18cd1", "#fbc2eb"], // лавандовый
  ["#fd7043", "#ff8a65"], // оранжевый
  ["#26a69a", "#80cbc4"], // изумрудный
];

function getGradient(userId) {
  if (!userId) return AVATAR_GRADIENTS[0];
  const hash = String(userId)
    .split("")
    .reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) & 0xffff, 0);
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
}

function getInitials(username) {
  if (!username) return "?";
  const clean = username.trim();
  const name = clean.includes("@") ? clean.split("@")[0] : clean;
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// ── Читает кеш и чистит устаревшие dicebear-записи от старого хука ───────────
function readCache(cacheKey) {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (raw === null) return undefined; // нет записи → нужно идти на сервер
    const val = JSON.parse(raw);
    // Старый хук сохранял dicebear-URL как fallback — выбрасываем такой кеш
    if (typeof val === "string" && val.includes("dicebear.com")) {
      localStorage.removeItem(cacheKey);
      return undefined;
    }
    return val; // null (нет аватарки) или строка-URL (есть аватарка)
  } catch {
    return undefined;
  }
}

// ── Инлайновый хук (логика из useAvatar) ─────────────────────────────────────
function useAvatarUrl(userId, enabled) {
  const [avatarUrl, setAvatarUrl] = useState(() => {
    if (!userId || !enabled) return null;
    const cached = readCache(`avatar_${userId}`);
    return cached ?? null;
  });
  const [isLoading, setIsLoading] = useState(false);

  const load = useCallback(async () => {
    if (!userId || !enabled) return;

    const cacheKey = `avatar_${userId}`;
    const cached = readCache(cacheKey);
    if (cached !== undefined) {
      setAvatarUrl(cached);
      return;
    }

    try {
      setIsLoading(true);
      const res = await axios.get(`${API_URL}/api/avatar/${userId}`, { timeout: 3000 });

      if (res.data.has_custom_avatar && res.data.direct_url) {
        // Добавляем cache-bust чтобы браузер точно перечитал свежее фото
        const url = `${res.data.direct_url}&v=${Date.now()}`;
        setAvatarUrl(url);
        localStorage.setItem(cacheKey, JSON.stringify(url));
      } else {
        // Нет своей аватарки — явно запишем null чтобы не ходить снова
        setAvatarUrl(null);
        localStorage.setItem(cacheKey, JSON.stringify(null));
      }
    } catch {
      setAvatarUrl(null);
    } finally {
      setIsLoading(false);
    }
  }, [userId, enabled]);

  useEffect(() => { load(); }, [load]);

  return { avatarUrl, isLoading };
}

// ── Компонент ─────────────────────────────────────────────────────────────────
/**
 * Avatar
 *
 * Props:
 *   userId    {string|number}  — id пользователя
 *   size      {number}         — диаметр в px (default 48)
 *   enabled   {boolean}        — включить загрузку (default true)
 *   username  {string}         — имя/email для инициалов в fallback
 *   style     {object}         — дополнительные стили обёртки
 *   className {string}         — CSS-классы обёртки
 */
export default function Avatar({
  userId,
  size      = 48,
  enabled   = true,
  username  = "",
  style     = {},
  className = "",
}) {
  const { avatarUrl, isLoading } = useAvatarUrl(userId, enabled);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError]   = useState(false);

  // Сбрасываем imgLoaded при смене URL (новый пользователь)
  useEffect(() => { setImgLoaded(false); setImgError(false); }, [avatarUrl]);

  const [g1, g2]   = getGradient(userId);
  const initials   = getInitials(username);
  const fontSize   = Math.round(Math.max(size * 0.36, 10));
  const fontWeight = size < 28 ? 700 : 800;

  const wrapStyle = {
    width:          size,
    height:         size,
    borderRadius:   "50%",
    flexShrink:     0,
    display:        "inline-flex",
    alignItems:     "center",
    justifyContent: "center",
    overflow:       "hidden",
    position:       "relative",
    ...style,
  };

  // Показываем реальную фотку если она успешно загружена
  const showPhoto = avatarUrl && !imgError;

  return (
    <div style={wrapStyle} className={className}>

      {/* ── Fallback-слой: всегда рендерится под фото ─────────────────────── */}
      {/* Виден пока фото грузится (isLoading / !imgLoaded) или когда фото нет */}
      <FallbackLayer
        g1={g1} g2={g2}
        initials={initials}
        fontSize={fontSize}
        fontWeight={fontWeight}
        visible={!showPhoto || !imgLoaded}
        pulse={isLoading && !avatarUrl}
      />

      {/* ── Реальная фотография ───────────────────────────────────────────── */}
      {showPhoto && (
        <img
          src={avatarUrl}
          alt={username || "avatar"}
          onLoad={()  => setImgLoaded(true)}
          onError={() => { setImgError(true); setImgLoaded(false); }}
          style={{
            position:     "absolute",
            inset:        0,
            width:        "100%",
            height:       "100%",
            objectFit:    "cover",
            borderRadius: "50%",
            opacity:      imgLoaded ? 1 : 0,
            transition:   "opacity 0.28s ease",
          }}
        />
      )}
    </div>
  );
}

// ── Fallback-слой: градиент + блик + инициалы ─────────────────────────────────
function FallbackLayer({ g1, g2, initials, fontSize, fontWeight, visible, pulse }) {
  return (
    <div
      aria-hidden={!visible}
      style={{
        position:       "absolute",
        inset:          0,
        borderRadius:   "50%",
        opacity:        visible ? 1 : 0,
        transition:     "opacity 0.25s ease",
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        overflow:       "hidden",
        // Пульсация только во время первого лоадинга (нет кеша)
        animation:      pulse ? "avatarPulse 1.6s ease-in-out infinite" : "none",
      }}
    >
      <style>{`
        @keyframes avatarPulse {
          0%, 100% { opacity: 0.55; }
          50%       { opacity: 0.90; }
        }
      `}</style>

      {/* Градиентный фон */}
      <div
        style={{
          position:     "absolute",
          inset:        0,
          borderRadius: "50%",
          background:   `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)`,
          boxShadow:    "inset 0 -2px 6px rgba(0,0,0,0.18), inset 0 1px 3px rgba(255,255,255,0.28)",
        }}
      />

      {/* Блик сверху */}
      <div
        style={{
          position:     "absolute",
          top:          0,
          left:         "15%",
          right:        "15%",
          height:       "44%",
          borderRadius: "0 0 60% 60%",
          background:   "rgba(255,255,255,0.20)",
          filter:       "blur(2px)",
          pointerEvents:"none",
        }}
      />

      {/* Инициалы */}
      <span
        style={{
          position:      "relative",
          zIndex:        1,
          fontSize,
          fontWeight,
          color:         "#ffffff",
          letterSpacing: initials.length > 1 ? "-0.5px" : "0",
          lineHeight:    1,
          userSelect:    "none",
          textShadow:    "0 1px 4px rgba(0,0,0,0.30)",
          fontFamily:    "'Inter', 'SF Pro Display', system-ui, sans-serif",
        }}
      >
        {initials}
      </span>
    </div>
  );
}